import { useEffect, useRef } from "react";

const GOLD = "#C9A84C";

export default function CustomCursor() {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Skip on touch devices
    if (window.matchMedia("(pointer: coarse)").matches) return;

    document.body.style.cursor = "none";

    let mouseX = 0, mouseY = 0;
    let curX = 0, curY = 0;
    let rafId = 0;

    const onMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (innerRef.current) {
        innerRef.current.style.left = `${mouseX}px`;
        innerRef.current.style.top = `${mouseY}px`;
      }
    };

    const onOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInteractive = !!target.closest("a, button, [role='button'], input, textarea, select, label");
      if (outerRef.current) {
        if (isInteractive) {
          outerRef.current.style.width = "52px";
          outerRef.current.style.height = "52px";
          outerRef.current.style.background = `${GOLD}18`;
          outerRef.current.style.borderColor = GOLD;
          outerRef.current.style.opacity = "1";
        } else {
          outerRef.current.style.width = "32px";
          outerRef.current.style.height = "32px";
          outerRef.current.style.background = "transparent";
          outerRef.current.style.borderColor = `${GOLD}CC`;
          outerRef.current.style.opacity = "0.75";
        }
      }
    };

    const onLeave = () => {
      if (outerRef.current) outerRef.current.style.opacity = "0";
      if (innerRef.current) innerRef.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (outerRef.current) outerRef.current.style.opacity = "0.75";
      if (innerRef.current) innerRef.current.style.opacity = "1";
    };

    const animate = () => {
      curX += (mouseX - curX) * 0.1;
      curY += (mouseY - curY) * 0.1;
      if (outerRef.current) {
        outerRef.current.style.left = `${curX}px`;
        outerRef.current.style.top = `${curY}px`;
      }
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    window.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      document.body.style.cursor = "";
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  return (
    <>
      {/* Outer ring — smoothly lagged */}
      <div
        ref={outerRef}
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 32,
          height: 32,
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          border: `2px solid ${GOLD}CC`,
          pointerEvents: "none",
          zIndex: 99999,
          opacity: 0,
          transition: "width 0.2s ease, height 0.2s ease, background 0.2s ease, opacity 0.2s ease",
          willChange: "left, top",
        }}
      />
      {/* Inner gold dot — instant tracking */}
      <div
        ref={innerRef}
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 6,
          height: 6,
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          background: GOLD,
          pointerEvents: "none",
          zIndex: 99999,
          opacity: 0,
          transition: "opacity 0.2s ease",
          willChange: "left, top",
        }}
      />
    </>
  );
}
